import { ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Skeleton, SkeletonRegion, StateBlock } from '../../components/ui';
import type { Stats } from './stats';
import type { Remote } from './useRemote';

interface PanelProps {
    title: string;
    /** One line under the title: what this card shows. */
    description?: string;
    action?: ReactNode;
    children: ReactNode;
    className?: string;
}

/** Card with a heading; the section is named by it. */
export const Panel = ({ title, description, action, children, className }: PanelProps) => {
    const headingId = useId();
    return (
        <section aria-labelledby={headingId} className={className}>
            <Card className="h-full">
                <div className="flex items-center justify-between gap-3">
                    <h2 id={headingId} className="text-title text-strong">
                        {title}
                    </h2>
                    {action}
                </div>
                {description && <p className="mt-0.5 text-small text-muted">{description}</p>}
                <div className="mt-3">{children}</div>
            </Card>
        </section>
    );
};

interface RemoteBodyProps<T> {
    remote: Remote<T>;
    skeleton: ReactNode;
    children: (data: T) => ReactNode;
}

/** Skeleton while loading, an error block with retry on failure, otherwise the content. One failed card does not blank the page. */
export function RemoteBody<T>({ remote, skeleton, children }: RemoteBodyProps<T>) {
    const { t } = useTranslation();
    if (remote.state === 'loading') return <SkeletonRegion>{skeleton}</SkeletonRegion>;
    if (remote.state === 'error' || remote.data === null) {
        return (
            <StateBlock
                variant="error"
                title={t('home:error.title')}
                body={t('ui.checkConnection')}
                action={
                    <Button variant="strong" onClick={remote.reload}>
                        {t('ui.retry')}
                    </Button>
                }
            />
        );
    }
    return <>{children(remote.data)}</>;
}

interface StatsBodyProps {
    remote: Remote<Stats>;
    children: (stats: Stats) => ReactNode;
    /** Skeleton height class; defaults to a chart. */
    skeletonClass?: string;
}

/** RemoteBody for a stats card: skeleton, error with retry, and "no orders in this period" instead of a flat chart. */
export const StatsBody = ({ remote, children, skeletonClass = 'h-48' }: StatsBodyProps) => {
    const { t } = useTranslation('home');
    return (
        <RemoteBody remote={remote} skeleton={<Skeleton className={`${skeletonClass} w-full`} />}>
            {(stats) =>
                stats.totals.orders === 0 ? (
                    <p className="py-6 text-center text-small text-muted">{t('empty.period')}</p>
                ) : (
                    children(stats)
                )
            }
        </RemoteBody>
    );
};
